import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Mail, RefreshCw, Send } from "lucide-react";

const RESENDABLE = new Set(["verification", "password_reset"]);

export default function AdminEmail() {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);
  const [resending, setResending] = useState("");

  const load = useCallback(async (dys) => {
    setLoading(true);
    setErr(false);
    try { const { data } = await api.get("/admin/email-log", { params: { days: dys } }); setD(data); }
    catch (e) { logger.error(formatApiError(e.response?.data?.detail) || e.message); setErr(true); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(days); }, [load, days]);

  const resend = async (id) => {
    setResending(id);
    try {
      const { data } = await api.post(`/admin/email-log/${id}/resend`);
      toast.success(`Resent ${data.kind.replace(/_/g, " ")} email.`);
      load(days);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Resend failed."); }
    finally { setResending(""); }
  };

  if (err) return <div className="py-20 text-center text-muted-foreground" data-testid="admin-email-error">Couldn't load the email log. Please retry.</div>;
  if (!d) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const stats = [
    { label: "Sent", value: d.sent, tone: "text-green-600" },
    { label: "Failed", value: d.failed, tone: "text-red-600" },
    { label: "Success rate", value: `${d.success_rate}%`, tone: "text-[#24468C]" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-email">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2"><Mail className="h-7 w-7 text-[#24468C]" strokeWidth={1.75} /> Email Deliverability</h1>
          <p className="text-muted-foreground text-sm">Transactional email send status (verification, password reset, referral, affiliate).</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-zinc-100 rounded-lg p-0.5" data-testid="email-range">
            {[[7, "7d"], [30, "30d"], [90, "90d"]].map(([v, l]) => (
              <button key={v} onClick={() => setDays(v)} className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${days === v ? "bg-white shadow-sm text-zinc-900" : "text-muted-foreground hover:text-zinc-700"}`} data-testid={`email-range-${v}`}>{l}</button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => load(days)} disabled={loading} data-testid="email-refresh-btn"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {stats.map((s) => (
          <Card key={s.label} className="border-zinc-200"><CardContent className="p-5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{s.label}</p>
            <p className={`font-heading text-3xl font-bold mt-1 ${s.tone}`} data-testid={`email-stat-${s.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>{s.value}</p>
          </CardContent></Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">By type</p>
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            {d.by_kind.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center" data-testid="email-bykind-empty">No emails sent in this window.</p> : (
              <table className="w-full text-sm" data-testid="email-bykind-table">
                <thead><tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-2.5 font-semibold">Type</th><th className="px-4 py-2.5 font-semibold text-right">Sent</th><th className="px-4 py-2.5 font-semibold text-right">Failed</th>
                </tr></thead>
                <tbody>{d.by_kind.map((k) => (
                  <tr key={k.kind} className="border-b border-zinc-100"><td className="px-4 py-2.5 capitalize">{k.kind.replace(/_/g, " ")}</td><td className="px-4 py-2.5 text-right text-green-600">{k.sent}</td><td className="px-4 py-2.5 text-right text-red-600">{k.failed || "—"}</td></tr>
                ))}</tbody>
              </table>
            )}
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Recent sends</p>
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden max-h-[400px] overflow-y-auto">
            {d.recent.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center" data-testid="email-recent-empty">No emails logged yet.</p> : (
              <table className="w-full text-sm" data-testid="email-recent-table">
                <thead><tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wider text-muted-foreground sticky top-0 bg-white">
                  <th className="px-4 py-2.5 font-semibold">When</th><th className="px-4 py-2.5 font-semibold">To</th><th className="px-4 py-2.5 font-semibold">Status</th><th className="px-4 py-2.5 font-semibold">Msg ID</th><th className="px-4 py-2.5 font-semibold"></th>
                </tr></thead>
                <tbody>{d.recent.map((e) => (
                  <tr key={e.id} className="border-b border-zinc-100" data-testid={`email-row-${e.id}`}>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{(e.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-2.5 text-xs font-mono">{e.to_masked}</td>
                    <td className="px-4 py-2.5">{e.ok ? <Badge className="bg-green-100 text-green-800">Sent</Badge> : <Badge className="bg-red-100 text-red-800" title={e.error}>Failed</Badge>}</td>
                    <td className="px-4 py-2.5 text-xs font-mono text-muted-foreground" title={e.provider_id || ""}>{e.provider_id ? `…${e.provider_id.slice(-8)}` : "—"}</td>
                    <td className="px-4 py-2.5 text-right">
                      {!e.ok && RESENDABLE.has(e.kind) && (
                        <Button variant="ghost" size="sm" className="h-7 text-xs" disabled={resending === e.id} onClick={() => resend(e.id)} data-testid={`email-resend-${e.id}`}>
                          {resending === e.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1" /> Resend</>}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
